import { schoolTeltAlsWerk, wouldBeWerkdag } from '../engine/rooster-engine.js';

// ── Cel-codes & kleuren ──────────────────────────────────────────────────────

export const CEL = {
  D:  { code: 'D',   label: 'Dagdienst',    bg: '#DBEAFE', fg: '#1e40af' },
  L:  { code: 'L',   label: 'Late dienst',  bg: '#FEF9C3', fg: '#854d0e' },
  R:  { code: 'R',   label: 'Rheden',       bg: '#EDE9FE', fg: '#5b21b6' },
  LW: { code: 'LW',  label: 'Lingewaard',   bg: '#CCFBF1', fg: '#0F766E' },
  S:  { code: 'S',   label: 'School',       bg: '#E0E7FF', fg: '#4338ca' },
  K:  { code: 'K',   label: 'Kantoor',      bg: '#F1F5F9', fg: '#64748b' },
  V:  { code: 'V',   label: 'Verlof',       bg: '#DCFCE7', fg: '#15803D' },
  Z:  { code: 'Z',   label: 'Ziek',         bg: '#FFEDD5', fg: '#C2410C' },
  A:  { code: 'A',   label: 'Anders',       bg: '#E2E8F0', fg: '#334155' },
  RI: { code: 'RI',  label: 'Re-integratie',bg: '#FAE8FF', fg: '#A21CAF' },
  G:  { code: 'G',   label: 'Geruild',      bg: '#FCE7F3', fg: '#9d174d' },
  OW: { code: 'OW',  label: 'Overwerk',     bg: '#FEF3C7', fg: '#92400e' },
};

// ── Palet voor instelbare dienst-kleuren ────────────────────────────────────
//
// Een vaste lijst benoemde kleuren, GEEN vrije kleurkiezer. De cel tekent de code
// met een vaste tekstkleur op de achtergrond; een zelfgekozen achtergrond kan daar
// onleesbaar mee worden (donkerblauw op donkerblauw, of geel op wit). Elk paletlid
// draagt daarom een bg/fg-paar dat samen getest is, in dezelfde stijl als de
// bestaande CEL-kleuren.
//
// Elke dienst-kleur uit CEL moet als paletsleutel aanwijsbaar zijn, anders verschiet
// de matrix zodra een bestaande dienst voor het eerst een expliciete code krijgt.
// Dat blijft gelden: blauw = D, geel = L, paars = R, en turkoois = LW sinds LW van
// groen naar turkoois ging (afwezigheids-kleurtaal, zie CEL hierboven).
//
// LET OP — sinds die kleurwijziging is de oude regel "de hues van V/Z/A zitten NIET
// in het palet" niet meer waar: `groen` (#DCFCE7) is nu de kleur van V/Verlof en
// `grijs` (#E2E8F0) die van A/Anders. Een dienst die zo'n paletkleur krijgt lijkt in
// de matrix op een afwezigheidscel. Vandaag is dat theorie — geen enkele dienst heeft
// een `cel_kleur` — maar wie deze twee uit het palet wil halen, moet dat bewust doen.
export const PALET = {
  blauw:    { naam: 'Blauw',    bg: '#DBEAFE', fg: '#1e40af' }, // = D
  geel:     { naam: 'Geel',     bg: '#FEF9C3', fg: '#854d0e' }, // = L
  paars:    { naam: 'Paars',    bg: '#EDE9FE', fg: '#5b21b6' }, // = R
  groen:    { naam: 'Groen',    bg: '#DCFCE7', fg: '#166534' }, // ≈ V (Verlof) — zie waarschuwing hierboven
  turkoois: { naam: 'Turkoois', bg: '#CCFBF1', fg: '#0f766e' }, // = LW
  roze:     { naam: 'Roze',     bg: '#FCE7F3', fg: '#9d174d' },
  amber:    { naam: 'Amber',    bg: '#FEF3C7', fg: '#92400e' },
  cyaan:    { naam: 'Cyaan',    bg: '#CFFAFE', fg: '#155e75' },
  limoen:   { naam: 'Limoen',   bg: '#ECFCCB', fg: '#3f6212' },
  grijs:    { naam: 'Grijs',    bg: '#E2E8F0', fg: '#334155' },
};

// Codes die een dienst NOOIT mag krijgen: ze zijn al bezet door een andere
// betekenis in getCel. Zet je een dienst op 'S', dan telt die cel via
// codeVan(...) === 'S' ineens mee in de School-rij van de dagweergave
// (Weekrooster.jsx) — stil fout, zonder melding.
export const GERESERVEERDE_CODES = ['V', 'Z', 'A', 'S', 'K'];

// Normalisatie van een ingetypte code: hoofdletters, alleen letters/cijfers, max 3.
export function normaliseerCelCode(waarde) {
  return String(waarde ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
}

// Paletsleutel bij een achtergrondkleur (voor het voorvullen vanuit de terugval).
export function paletSleutelVoorKleur(bg) {
  return Object.keys(PALET).find(k => PALET[k].bg === bg) || null;
}

// ── Werk of niet: BLACKLIST, geen whitelist ─────────────────────────────────
//
// Hiervoor stond op twee plekken een whitelist van werk-codes (['D','L','R','LW',
// 'S','K']). Die breekt STIL zodra er een code bijkomt: de nieuwe code staat er
// niet in, telt dus als niet-werk, en berekenMarkeringDelta (Weekrooster.jsx)
// rekent er 8 uur naast in Gepland/Balans zonder dat er iets faalt. Omgekeerd is
// de regel stabiel en zelf-uitbreidend: alleen afwezigheid is geen werk, al het
// andere wel. Voor élke code die getCel vandaag kan teruggeven (D/L/R/LW/S/K/V/
// Z/A) is de uitkomst identiek aan de oude whitelist — RI/G/OW worden nergens
// geproduceerd, dus daar verandert feitelijk niets.
export const AFWEZIG_CODES = ['V', 'Z', 'A'];

// ── Afwezigheid buiten de matrix: één kleurtaal ─────────────────────────────
//
// De matrix tekent afwezigheid via getCel → CEL. De dienst-rijen in TeamRooster,
// Mijn Rooster en de dag-weergave lezen RAUW uit het rooster en gaan dus niet door
// getCel heen (zie de comment bij DagWeergave): daar blijft de naam staan, maar
// doorgestreept. Die vier plekken hadden elk hun eigen `labelVoor` en dezelfde vaste
// grijze streep, waardoor het type wel in de tekst stond maar niet in de kleur.
//
// Deze helper is de gedeelde bron. De kleur komt UIT CEL, zodat matrix, legenda,
// Excel-export en doorstreping niet uit elkaar kunnen lopen: één plek wijzigen
// volstaat. Hex en geen Tailwind-klasse, omdat de matrix (Weekrooster) en de legenda
// de CEL-kleuren ook inline toepassen en de Excel-export ze via argb() leest.
//
// Onbekend of ontbrekend type → 'anders' (grijs). Bewust neutraal: eerder zou een
// onbekende waarde als 'verlof' zijn gelabeld, en dat is een bewering te veel.
export const AFWEZIG_TYPE = {
  verlof: { code: 'V', label: 'verlof' },
  ziekte: { code: 'Z', label: 'ziek' },
  anders: { code: 'A', label: 'afwezig' },
};

export function afwezigStijl(type) {
  const t = AFWEZIG_TYPE[type] || AFWEZIG_TYPE.anders;
  return { code: t.code, label: t.label, kleur: CEL[t.code].fg };
}

export function isWerkCode(code) {
  return !!code && !AFWEZIG_CODES.includes(code);
}

// ── Daglijst-groepen ────────────────────────────────────────────────────────
//
// De vaste groepen en hun volgorde zijn 1-op-1 overgenomen uit de export (let op
// de hoofdletter in 'Late Dienst'), zodat een bestaande Excel identiek blijft.
const DAGLIJST_GROEP_VAST = {
  D: 'Dagdienst', L: 'Late Dienst', LW: 'Lingewaard', R: 'Rheden', K: 'Kantoor', S: 'School',
};
export const DAGLIJST_VOLGORDE_VAST = ['Dagdienst', 'Late Dienst', 'Lingewaard', 'Rheden', 'Kantoor', 'School'];

// Groep van één cel. Geeft NOOIT undefined voor een echte cel: een code zonder
// vaste groep valt terug op zijn eigen label. De oude lookup (DAGLIJST_GROEP[code])
// gaf undefined voor een onbekende code, waarna de export die persoon stil uit de
// daglijst liet vallen — dat kan hiermee niet meer.
export function daglijstGroep(celObj) {
  if (!celObj?.code) return null;
  if (AFWEZIG_CODES.includes(celObj.code)) return 'afwezig';
  return DAGLIJST_GROEP_VAST[celObj.code] || celObj.label || celObj.code;
}

// Volgorde voor een concrete set groepen: eerst de vaste zes in hun vaste volgorde,
// daarna onbekende groepen alfabetisch — deterministisch, en achteraan zodat de
// bestaande export ongewijzigd blijft.
export function daglijstVolgorde(aanwezigeGroepen = []) {
  const extra = [...new Set(aanwezigeGroepen)]
    .filter(g => g && g !== 'afwezig' && !DAGLIJST_VOLGORDE_VAST.includes(g))
    .sort((a, b) => a.localeCompare(b, 'nl'));
  return [...DAGLIJST_VOLGORDE_VAST, ...extra];
}

// ── Legenda-bron ────────────────────────────────────────────────────────────
// BASIS = alles wat getCel vandaag kan teruggeven, in de volgorde waarin het altijd
// al getoond werd. RESERVE = wel gedefinieerd, maar door niets geproduceerd. De
// Excel toont alleen BASIS, het scherm beide — exact de twee losse lijsten van
// hiervoor, nu uit één bron.
export const LEGENDA_BASIS   = ['D', 'L', 'R', 'LW', 'S', 'K', 'V', 'Z', 'A'];
export const LEGENDA_RESERVE = ['RI', 'G', 'OW'];

// ── Code per dienst ─────────────────────────────────────────────────────────
//
// Waarom Arnhem op DIENST splitst (dagdienst → D, de rest → L) en Rheden/Lingewaard
// op GEMEENTE (elke dienst → R resp. LW): dat was een keuze van toen, geen regel.
// Arnhem draait twee soorten diensten op dezelfde dag (dagdienst 09-17 én late
// 14-22), dus daar moest de cel het verschil tonen; Rheden en Lingewaard hadden er
// maar één soort tegelijk, dus daar was de gemeentenaam informatiever dan het
// dienst-type. Die asymmetrie zit nu in de TERUGVAL, niet meer in getCel zelf.
//
// Volgorde: expliciet veld op de dienst → terugval. `cel_code` (+ `cel_kleur`, een
// paletsleutel) worden in DienstModal ingesteld en zijn beide optioneel: staat er
// geen code, dan geldt de terugval en verandert er niets aan bestaande data.

// Neutrale kleur voor een gemeente die (nog) geen eigen code heeft.
const NEUTRAAL = { bg: '#E2E8F0', fg: '#334155' };

// De terugval: exact de mapping die vóór de instelbare code gold. Ongewijzigd.
function terugvalCel(regels, gemKey, dienstId) {
  if (gemKey === 'arnhem')     return dienstId === 'dagdienst' ? CEL.D : CEL.L;
  if (gemKey === 'rheden')     return CEL.R;
  if (gemKey === 'lingewaard') return CEL.LW;

  // Onbekende gemeente: code afgeleid uit de displaynaam (eerste twee letters, net
  // als LW voor Lingewaard), label = de gemeentenaam, eigen neutrale kleur.
  // Deterministisch uit de data — dezelfde gemeente geeft altijd dezelfde cel.
  const naam = regels?.gemeenten?.[gemKey]?.naam || gemKey;
  const letters = String(naam).replace(/[^A-Za-z]/g, '');
  const code = (letters.slice(0, 2) || String(gemKey).slice(0, 2) || '?').toUpperCase();
  return { code, label: String(naam), ...NEUTRAAL };
}

export function celCodeVoorDienst(regels, gemKey, dienstId) {
  const dienst = regels?.gemeenten?.[gemKey]?.diensten?.[dienstId];
  const terugval = terugvalCel(regels, gemKey, dienstId);
  const code = normaliseerCelCode(dienst?.cel_code);
  if (!code) return terugval;

  // Kleur: paletsleutel → anders de kleur van een gelijknamige standaardcode →
  // anders neutraal. Zo blijft een handmatig gezette code zonder kleur leesbaar.
  const kleur = PALET[dienst?.cel_kleur] || (CEL[code] ? { bg: CEL[code].bg, fg: CEL[code].fg } : NEUTRAAL);

  // Label: een expliciete code die GELIJK is aan wat de terugval al gaf houdt het
  // terugval-label. Anders zou een bestaande dienst die zijn eigen code krijgt
  // stil van legenda-tekst en Excel-daglijstgroep veranderen (Rheden → 'Late
  // dienst'), terwijl er zichtbaar niets zou moeten wijzigen. Pas bij een ANDERE
  // code gaat het label de dienst beschrijven.
  const label = code === terugval.code ? terugval.label : (dienst?.label?.trim() || dienstId);
  return { code, label, bg: kleur.bg, fg: kleur.fg };
}

// Alle diensten van een omgeving, in vaste volgorde: gemeenten in data-volgorde,
// daarbinnen dagdienst voorop (zelfde volgorde als getCel aanhoudt).
function alleDiensten(regels) {
  const uit = [];
  for (const [gemKey, gem] of Object.entries(regels?.gemeenten || {})) {
    if (gemKey.startsWith('_')) continue;
    for (const dienstId of dienstenInVolgorde(gem.diensten || {})) {
      uit.push({ gemKey, gem, dienstId, dienst: gem.diensten[dienstId] });
    }
  }
  return uit;
}

// Welke cel-codes zijn in deze omgeving al in gebruik, en door wie?
// LET OP: afgeleid via celCodeVoorDienst over ALLE diensten, niet uit het
// cel_code-veld alleen. D/L/R/LW staan nergens geregistreerd zolang die diensten
// geen expliciete code hebben — zonder deze afleiding zou je 'R' aan een nieuwe
// gemeente kunnen geven terwijl Rheden hem via de terugval al toont.
export function gebruikteCelCodes(regels) {
  const uit = new Map();
  for (const { gemKey, gem, dienstId, dienst } of alleDiensten(regels)) {
    const cel = celCodeVoorDienst(regels, gemKey, dienstId);
    if (!cel?.code) continue;
    const lijst = uit.get(cel.code) || [];
    lijst.push({
      gemKey,
      dienstId,
      gemNaam: gem.naam || gemKey,
      dienstLabel: dienst?.label?.trim() || dienstId,
    });
    uit.set(cel.code, lijst);
  }
  return uit;
}

// Diensten gegroepeerd op cel-code — één groep wordt één rij in de dag-weergave.
// Groeperen op CODE en niet per dienst, want meerdere diensten kunnen dezelfde cel
// opleveren: Arnhem heeft late_dienst_dw + late_dienst_we (allebei L) en Lingewaard
// late_dienst + dagdienst (allebei LW). Die horen bij elkaar in één rij, ongeacht
// gemeente of dag-opbouw (gewone `dagen` of een week_a/week_b-cyclus).
//
// Twee volgordes, allebei bewust:
//  - RIJ-volgorde = eerste voorkomen in alleDiensten (dagdienst voorop). Dat is
//    dezelfde volgorde die legendaCodesVoor aanhoudt, dus rijen en legenda lopen
//    gelijk: D, L, R, LW.
//  - LEDEN binnen een groep = de volgorde waarin de diensten in de data staan, want
//    zo staan de namen ook in het rooster (de engine bouwt rooster[gem][dienst] in
//    data-volgorde). Voor Lingewaard verschilt dat van de rij-volgorde: daar staat
//    late_dienst vóór dagdienst in de data.
// Geef `rooster` mee om ook VERWEESDE paren mee te nemen: (gemeente, dienst) die nog
// wél in een vastgelegde week staan maar niet meer in regels. Zonder die tak verliest
// de dag-weergave namen die de MATRIX nog gewoon toont — getCel loopt over het rooster
// en vindt ze dus wel — en dat gebeurt zonder enige melding. Ze komen achteraan, dus
// aan de normale weergave verandert het niets.
export function dienstGroepenPerCode(regels, rooster = null) {
  const groepen = new Map(); // code → { cel, diensten: [{ gemKey, dienstId }] }
  const bekend = new Set();  // `gemKey|dienstId` die in regels staan

  for (const { gemKey, dienstId } of alleDiensten(regels)) {
    const cel = celCodeVoorDienst(regels, gemKey, dienstId);
    if (cel?.code && !groepen.has(cel.code)) groepen.set(cel.code, { cel, diensten: [] });
  }

  for (const [gemKey, gem] of Object.entries(regels?.gemeenten || {})) {
    if (gemKey.startsWith('_')) continue;
    for (const dienstId of Object.keys(gem.diensten || {})) {
      bekend.add(`${gemKey}|${dienstId}`);
      const groep = groepen.get(celCodeVoorDienst(regels, gemKey, dienstId)?.code);
      if (groep) groep.diensten.push({ gemKey, dienstId });
    }
  }

  // Verweesde paren: alfabetisch op gemeente + dienst, zodat de rij-volgorde en de
  // namen-volgorde ook hier niet van toeval afhangen. Een verweesd paar sluit aan bij
  // de groep van zijn cel-code (een verwijderde Rheden-dienst valt via de terugval nog
  // steeds op R), en krijgt een eigen rij achteraan als die code nog niet bestaat.
  const wees = [];
  for (const [gemKey, gemRooster] of Object.entries(rooster || {})) {
    if (gemKey.startsWith('_')) continue;
    for (const dienstId of Object.keys(gemRooster || {})) {
      if (!bekend.has(`${gemKey}|${dienstId}`)) wees.push({ gemKey, dienstId });
    }
  }
  wees.sort((a, b) => a.gemKey.localeCompare(b.gemKey, 'nl') || a.dienstId.localeCompare(b.dienstId, 'nl'));

  for (const { gemKey, dienstId } of wees) {
    const cel = celCodeVoorDienst(regels, gemKey, dienstId);
    if (!cel?.code) continue;
    if (!groepen.has(cel.code)) groepen.set(cel.code, { cel, diensten: [] });
    groepen.get(cel.code).diensten.push({ gemKey, dienstId });
  }

  return [...groepen.values()];
}

// Voorstel voor een vrije code bij een nieuwe dienst: afgeleid uit de gemeentenaam
// (zelfde afleiding als de onbekende-gemeente-terugval), en anders steeds een letter
// langer/korter. Alles bezet → lege string, dan typt de gebruiker er zelf een.
export function suggestieCelCode(regels, gemKey) {
  const naam = regels?.gemeenten?.[gemKey]?.naam || gemKey || '';
  const letters = String(naam).replace(/[^A-Za-z]/g, '').toUpperCase();
  const bezet = gebruikteCelCodes(regels);
  const kandidaten = [letters.slice(0, 2), letters.slice(0, 3), letters.slice(0, 1)]
    .map(normaliseerCelCode)
    .filter(Boolean);
  return kandidaten.find(c => !GERESERVEERDE_CODES.includes(c) && !bezet.has(c)) || '';
}

// Voorstel voor een nog ongebruikte paletkleur.
export function suggestieCelKleur(regels) {
  const bezet = new Set();
  for (const { gemKey, dienstId } of alleDiensten(regels)) {
    const sleutel = paletSleutelVoorKleur(celCodeVoorDienst(regels, gemKey, dienstId).bg);
    if (sleutel) bezet.add(sleutel);
  }
  const sleutels = Object.keys(PALET);
  return sleutels.find(k => !bezet.has(k)) || sleutels[0];
}

// De legenda van DEZE omgeving: elke code die er echt voorkomt (alle diensten via
// celCodeVoorDienst), gevolgd door de vaste S/K/V/Z/A. Zonder regels valt hij terug
// op de statische basislijst. Met de huidige data levert dit exact LEGENDA_BASIS op:
// arnhem (dagdienst→D, late→L), rheden→R, lingewaard→LW, daarna S/K/V/Z/A.
const LEGENDA_STAART = ['S', 'K', 'V', 'Z', 'A'];

export function legendaCodesVoor(regels) {
  if (!regels?.gemeenten) return LEGENDA_BASIS.map(c => CEL[c]);
  const uit = [];
  const gezien = new Set();
  const voegToe = (cel) => {
    if (!cel?.code || gezien.has(cel.code)) return;
    gezien.add(cel.code);
    uit.push(cel);
  };
  for (const { gemKey, dienstId } of alleDiensten(regels)) voegToe(celCodeVoorDienst(regels, gemKey, dienstId));
  for (const c of LEGENDA_STAART) voegToe(CEL[c]);
  return uit;
}

// ── Zoekvolgorde in het rooster ─────────────────────────────────────────────
//
// DE VOLGORDE IS GEDRAG, GEEN TOEVAL. Staat iemand op één dag in twee gemeenten,
// dan bepaalt deze volgorde welke cel je ziet. De engine voorkomt dat normaal
// (vulSlot in rooster-engine.js slaat een kandidaat over die die dag al ergens staat)
// en analyseerWeek meldt het alsnog als 'dubbel' (analyseer-week.js), maar
// handmatig bewerken of geïmporteerde data kan het opleveren. Historisch won
// lingewaard van rheden van arnhem — dat was de volgorde van de oude if-reeks.
// Die tie-break houden we exact aan; overige gemeenten komen erachter, alfabetisch.
const GEM_VOLGORDE = ['lingewaard', 'rheden', 'arnhem'];

function gemeentenInVolgorde(rooster) {
  const keys = Object.keys(rooster);
  const bekend = GEM_VOLGORDE.filter(g => keys.includes(g));
  const rest = keys.filter(g => !GEM_VOLGORDE.includes(g)).sort((a, b) => a.localeCompare(b, 'nl'));
  return [...bekend, ...rest];
}

// Binnen één gemeente gaat 'dagdienst' voorop: de oude code checkte arnhem.dagdienst
// expliciet vóór de overige Arnhem-diensten, dus bij een dubbele plaatsing binnen
// één gemeente won de dagdienst. De rest volgt in data-volgorde (stabiel per config).
function dienstenInVolgorde(gemRooster) {
  const ids = Object.keys(gemRooster);
  return [...ids.filter(id => id === 'dagdienst'), ...ids.filter(id => id !== 'dagdienst')];
}

// ── Handmatige celmarkering: de code, en bij Kantoor een eigen urenaantal ──────
//
// TWEE OPSLAGVORMEN, allebei geldig. Daarom deze twee helpers, en nergens meer een
// `===` op de rauwe waarde:
//   'K' | 'S' | 'leeg'        de oorspronkelijke vorm — een K daarin is 8 uur
//   { code: 'K', uren: 3 }    sinds het urenaantal instelbaar werd
//
// ALLEEN Kantoor draagt uren. 'S' en 'leeg' blijven de kale string die ze altijd waren,
// en school houdt zijn eigen regeling (mw.school.uren, rooster-engine.js:354) — dat staat
// hier volledig los van.
//
// Er wordt NIETS gemigreerd: een bestaande "K" blijft een string en valt via
// overrideUren terug op 8. Beide helpers zijn daarom bewust tolerant — een onbekende vorm
// geeft null respectievelijk de standaard, zodat geen enkele lezer struikelt over data uit
// een oudere back-up.
export const KANTOOR_UREN_STANDAARD = 8;
export const KANTOOR_UREN_MIN = 0.5;
export const KANTOOR_UREN_MAX = 12;
export const KANTOOR_UREN_STAP = 0.5;

/** De code van een markering ('K' | 'S' | 'leeg'), of null als er geen markering ligt. */
export function overrideCode(override) {
  if (!override) return null;
  if (typeof override === 'string') return override;
  return override.code ?? null;
}

/**
 * Het urenaantal van een markering, altijd een getal.
 *
 * Terugval op KANTOOR_UREN_STANDAARD, en dat is de hele afspraak rond bestaande data: een
 * "K" zonder urenveld is en blijft 8 uur. Dezelfde terugval geldt voor 'S' en voor een
 * object waarin `uren` ontbreekt of geen eindig getal is.
 */
export function overrideUren(override) {
  if (override && typeof override === 'object' && Number.isFinite(override.uren)) return override.uren;
  return KANTOOR_UREN_STANDAARD;
}

// `regels` staat achteraan (optioneel) zodat geen enkele bestaande argument-positie
// verschuift; wouldBeWerkdag heeft het nodig om de vast_rotatiepatroon-weekenddiensten
// (Haruun/Myrthe op Lingewaard za/zo in week A) als werkdag te herkennen. Sinds de
// dienst-gedreven cel-code leest ook celCodeVoorDienst eruit — zonder `regels` valt
// die terug op dezelfde mapping als hiervoor, dus oude aanroepen blijven werken.
export function getCel(naam, dag, rooster, mw, weekType, weeknummer, afwTypePersoon, override, regels = null, vakantieDagen = null) {
  // Afwezigheid heeft VOORRANG op S/K/dienst: op een afwezige would-be-werkdag toont
  // de cel V/Z/A; structureel vrije dagen (geen would-be-werkdag) blijven leeg.
  const afwType = afwTypePersoon?.[dag];
  if (afwType && wouldBeWerkdag(mw, dag, weekType, weeknummer, regels, vakantieDagen)) {
    if (afwType === 'ziekte') return CEL.Z;
    if (afwType === 'verlof') return CEL.V;
    return CEL.A; // 'anders'
  }
  // Handmatige cel-markering (stukje 2/3): NA de afwezigheid-tak, VOOR de S/K/dienst-logica.
  // De teamleider kan S/K expliciet zetten of een cel leegmaken; undefined valt door.
  // Via overrideCode, want een K kan sinds het instelbare urenaantal ook een object zijn.
  // Voor de CODE maakt dat niets uit — die bepaalt alleen welke badge de cel krijgt; de uren
  // spelen pas een rol in de urenbalans (berekenMarkeringDelta in Weekrooster.jsx).
  const overrideCodeWaarde = overrideCode(override);
  if (overrideCodeWaarde === 'S') return CEL.S;
  if (overrideCodeWaarde === 'K') return CEL.K;
  if (overrideCodeWaarde === 'leeg') return null;
  if (rooster) {
    for (const gemKey of gemeentenInVolgorde(rooster)) {
      const gemRooster = rooster[gemKey];
      if (!gemRooster) continue;
      for (const dienstId of dienstenInVolgorde(gemRooster)) {
        if (gemRooster[dienstId]?.[dag]?.includes(naam)) {
          return celCodeVoorDienst(regels, gemKey, dienstId);
        }
      }
    }
  }
  // School NA het rooster (net als de kantoor-tak hieronder): een ECHTE dienst wint van
  // school, zodat een handmatig toegevoegde dienst zichtbaar blijft in de matrix i.p.v.
  // afgevangen te worden door S. Een schooldag ZONDER dienst valt door het rooster-blok
  // heen en geeft hier alsnog S — week A bij de afwisselende regeling; in week B telt
  // school als eigen tijd (schoolTeltAlsWerk = false) → geen S, de cel blijft leeg.
  // In een schoolvakantie vervalt de schooldag helemaal (schoolTeltAlsWerk = false via
  // vakantieDagen) → ook geen S; de cel is dan een gewone, vrij inzetbare dag.
  if (mw?.school?.dag === dag && schoolTeltAlsWerk(mw, weekType, vakantieDagen)) return CEL.S;
  if (mw?.kantoor_dagen?.includes(dag)) return CEL.K;
  return null;
}
